/**
 * 🎨 کیت طراحی «دستیار گروه‌ها»
 *
 * زبان بصری این ماژول دقیقاً همان زبان صفحهٔ حسابداری است: کارت با سربرگ آیکن‌دار،
 * کاشی شاخص با نوار رنگی و راهنمای «؟»، جمله‌های خودکار، ردیف‌های خطی فشرده و
 * توضیح زبان‌ساده. کلاس‌های `fac-*` از `pages/FinanceAccounting.css` می‌آیند —
 * آن فایل زبان بصری مشترک هر دو ماژول است، پس قبل از تغییرش هر دو را ببینید.
 */
import React from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography, alpha } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { toPersianDigits } from '../common/JalaliDatePicker';
import type { UserbotLocalIntent } from '../../services/userbotService';
import '../../pages/FinanceAccounting.css';

// همان بلوک‌های صفحهٔ حسابداری، تا هر دو ماژول یک شکل باشند
export { Explain, Insights, Kpis } from '../../pages/FinanceAccountingKit';
export type { Tone, Kpi as FacKpi } from '../../pages/FinanceAccountingKit';

// ───────────────────────────── ابزارهای متن ─────────────────────────────

export const fa = (v: any) => toPersianDigits(String(v ?? ''));

export const faDate = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return toPersianDigits(new Date(iso).toLocaleString('fa-IR', { dateStyle: 'short', timeStyle: 'short' }));
  } catch {
    return '—';
  }
};

export const clock = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return toPersianDigits(new Date(iso).toLocaleTimeString('fa-IR', { hour: '2-digit', minute: '2-digit' }));
  } catch {
    return '';
  }
};

export const dayLabel = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fa-IR', { month: 'long', day: 'numeric' });
  } catch {
    return '';
  }
};

export const relTime = (iso?: string | null): string => {
  if (!iso) return '—';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'همین الان';
  if (d < 3600) return `${fa(Math.round(d / 60))} دقیقه پیش`;
  if (d < 86400) return `${fa(Math.round(d / 3600))} ساعت پیش`;
  if (d < 7 * 86400) return `${fa(Math.round(d / 86400))} روز پیش`;
  return faDate(iso);
};

export const toman = (n?: number | null): string =>
  n === null || n === undefined || !Number.isFinite(Number(n))
    ? '—'
    : `${toPersianDigits(Math.round(Number(n)).toLocaleString('fa-IR'))} تومان`;

/** عدد فشرده برای کاشی‌ها: ۱۲٫۳ هزار */
export const compactNum = (n?: number | null): string => {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) >= 1_000_000) return `${fa((v / 1_000_000).toFixed(1))}M`;
  if (Math.abs(v) >= 10_000) return `${fa((v / 1000).toFixed(1))}K`;
  return fa(v);
};

export const errMsg = (e: unknown, fallback = 'خطا در انجام عملیات'): string =>
  e instanceof Error && e.message ? e.message : fallback;

/** حرف اول نام برای آواتار */
export const initial = (name?: string): string => (name || '؟').trim().charAt(0).toUpperCase();

/** رنگ ثابت و تکرارپذیر برای هر شناسه */
export const hueOf = (id: string): string => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h + id.charCodeAt(i) * 7) % 360;
  return `hsl(${h}, 52%, 46%)`;
};

// ───────────────────────────── رنگ‌های معنایی ─────────────────────────────
// همان پالت توکن‌های variables.css تا با صفحهٔ حسابداری یکی باشد

export const UB = {
  radius: 3,
  ok: '#16a34a',
  warn: '#d97706',
  bad: '#dc2626',
  info: '#0284c7',
  muted: '#6b7280',
  accent: '#9333ea',
};

/** نام کلاس کاشی از روی رنگ معنایی */
const kpiClass = (c?: string): string =>
  c === UB.ok ? 'green' : c === UB.bad ? 'red' : c === UB.warn ? 'orange' : c === UB.accent ? 'purple' : c === UB.muted ? 'gray' : 'blue';

// ───────────────────────────── کارت بخش ─────────────────────────────

export const SectionCard: React.FC<{
  title?: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  /** بدون پدینگ داخلی (برای فهرست‌های تمام‌عرض) */
  flush?: boolean;
  sx?: any;
  children: React.ReactNode;
}> = ({ title, hint, icon, actions, flush, sx, children }) => (
  <div className="fac-card" style={sx?.minHeight ? { minHeight: sx.minHeight } : undefined}>
    {(title || actions) && (
      <div className="fac-card-head">
        {icon}
        {title && <h3>{title}</h3>}
        {actions && <div className="fac-actions">{actions}</div>}
        {hint && <p>{hint}</p>}
      </div>
    )}
    <div className="fac-card-body" style={flush ? { padding: 0 } : undefined}>{children}</div>
  </div>
);

// ───────────────────────────── تایل آمار ─────────────────────────────

export const StatTile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  color?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}> = ({ label, value, hint, color, icon, onClick }) => (
  <div
    className={`fac-kpi ${kpiClass(color)}`}
    onClick={onClick}
    style={onClick ? { cursor: 'pointer' } : undefined}
  >
    <div className="fac-kpi-label">
      {icon && <span style={{ display: 'inline-flex', color: color || 'var(--color-primary)' }}>{icon}</span>}
      {label}
    </div>
    <div className="fac-kpi-value">{value}</div>
    {hint && <div className="fac-kpi-sub">{hint}</div>}
  </div>
);

/** گرید کاشی‌ها — همان چیدمان صفحهٔ حسابداری */
export const Tiles: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="fac-kpis">{children}</div>
);

// ───────────────────────────── ردیف‌های خطی ─────────────────────────────

/** جدول ردیفی فشرده (همان «دفتر فروش») — cols = grid-template-columns */
export const Rows: React.FC<{ cols: string; minWidth?: number; children: React.ReactNode }> = ({ cols, minWidth = 900, children }) => (
  <div className="fac-rows-wrap">
    <div className="fac-rows" style={{ minWidth }}>
      {React.Children.map(children, (ch) =>
        React.isValidElement(ch) ? React.cloneElement(ch as any, { _cols: cols }) : ch,
      )}
    </div>
  </div>
);

export const Row: React.FC<{
  head?: boolean;
  muted?: boolean;
  active?: boolean;
  onClick?: () => void;
  title?: string;
  children: React.ReactNode;
  /** از Rows تزریق می‌شود */
  _cols?: string;
}> = ({ head, muted, active, onClick, title, children, _cols }) => (
  <div
    className={`fac-row ${head ? 'head' : ''} ${muted ? 'inactive' : ''} ${active ? 'open' : ''}`}
    style={{ gridTemplateColumns: _cols, cursor: onClick ? 'pointer' : 'default' }}
    onClick={onClick}
    title={title}
  >
    {children}
  </div>
);

/** سلول دوخطی: عنوان پررنگ + زیرنویس */
export const Cell: React.FC<{ top: React.ReactNode; sub?: React.ReactNode; mono?: boolean }> = ({ top, sub, mono }) => (
  <span className="fac-row-party">
    <b style={mono ? { fontFamily: 'ui-monospace, Menlo, monospace', direction: 'ltr', textAlign: 'right' } : undefined}>{top}</b>
    {sub != null && sub !== '' && <small>{sub}</small>}
  </span>
);

/** ریزآمار چسبیده (عدد + برچسب) */
export const Mini: React.FC<{ value: React.ReactNode; label: string; title?: string }> = ({ value, label, title }) => (
  <span className="ubc-mini" title={title}><b>{value}</b><i>{label}</i></span>
);

// ───────────────────────────── چیپ‌ها و نشانه‌ها ─────────────────────────────

export const Pill: React.FC<{ label: React.ReactNode; color?: string; icon?: React.ReactElement; title?: string }> = ({
  label,
  color = UB.muted,
  icon,
  title,
}) => (
  <span
    className="fac-tag"
    title={title}
    style={{ background: alpha(color, 0.12), color, border: `1px solid ${alpha(color, 0.25)}` }}
  >
    {icon && React.cloneElement(icon, { style: { fontSize: 13, ...(icon.props as any)?.style } })}
    {label}
  </span>
);

export const Tag: React.FC<{ label: React.ReactNode; title?: string; color?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' }> = ({
  label,
  title,
  color = 'default',
}) => {
  const cls = color === 'success' ? 'green' : color === 'error' ? 'red' : color === 'warning' ? 'orange' : color === 'primary' ? 'blue' : color === 'secondary' ? 'blue' : 'gray';
  return <span className={`fac-tag ${cls}`} title={title}>{label}</span>;
};

/** نشانگر سلامت — سبز/نارنجی/قرمز با برچسب */
export const Dot: React.FC<{ ok: boolean; label: string; warn?: boolean; hint?: string }> = ({ ok, label, warn, hint }) => {
  const color = ok ? UB.ok : warn ? UB.warn : UB.bad;
  const Icon = ok ? CheckCircleRoundedIcon : warn ? WarningRoundedIcon : ErrorRoundedIcon;
  const body = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
      <Icon style={{ fontSize: 16, color }} />
      {label}
    </span>
  );
  return hint ? <Tooltip title={hint} arrow>{body}</Tooltip> : body;
};

/** چیپ‌های تحلیل نیت محلی: چه می‌خواهد و چه چیزی */
export const LocalChips: React.FC<{ local?: UserbotLocalIntent }> = ({ local }) => {
  if (!local) return null;
  const items = [
    local.object,
    local.brand,
    local.concern,
    local.skinType,
    local.budget ? `بودجه ${local.budget}` : '',
  ].filter(Boolean) as string[];
  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
      <Pill
        label={`${INTENT_FA[local.intent] || local.intent} ${fa(Math.round(local.confidence * 100))}٪`}
        color={local.intent === 'none' ? UB.muted : UB.accent}
        title={local.signals?.join(' · ')}
      />
      {items.map((t) => <Tag key={t} label={t} />)}
      {local.isQuestion && <Tag label="سؤال" />}
      {local.isAd && <Tag color="error" label="آگهی فروشنده" />}
      {local.offDomain && <Tag color="warning" label="خارج از حوزه" />}
    </span>
  );
};

// ───────────────────────────── حباب پیام ─────────────────────────────

export const Bubble: React.FC<{
  ours?: boolean;
  who?: React.ReactNode;
  text: string;
  at?: string;
  highlight?: boolean;
  foot?: React.ReactNode;
}> = ({ ours, who, text, at, highlight, foot }) => (
  <div className={`ubc-bubble-row ${ours ? 'ours' : ''}`}>
    <div className={`ubc-bubble ${ours ? 'ours' : ''} ${highlight ? 'hl' : ''}`}>
      {who && <div className="ubc-bubble-who">{who}</div>}
      <div className="ubc-bubble-text">{text}</div>
      {(foot || at) && (
        <div className="ubc-bubble-foot">
          {foot}
          <span style={{ flex: 1 }} />
          {at && <span className="fac-muted">{clock(at)}</span>}
        </div>
      )}
    </div>
  </div>
);

// ───────────────────────────── نوار ابزار و حالت خالی ─────────────────────────────

export const Toolbar: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5, ...sx }}>{children}</Box>
);

export const EmptyHint: React.FC<{ icon?: React.ReactNode; title: string; hint?: string; action?: React.ReactNode }> = ({
  icon,
  title,
  hint,
  action,
}) => (
  <div className="fac-empty">
    {icon && <div style={{ fontSize: 0, opacity: 0.3, marginBottom: 6 }}><span style={{ display: 'inline-flex', fontSize: 42 }}>{icon}</span></div>}
    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--color-text-primary)' }}>{title}</div>
    {hint && <div style={{ fontSize: 11.5, marginTop: 4 }}>{hint}</div>}
    {action && <div style={{ marginTop: 12 }}>{action}</div>}
  </div>
);

export const Loading: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <div className="fac-loading"><CircularProgress size={size} /></div>
);

/** دکمهٔ آیکنی با تولتیپ — تکرار همیشگی این صفحه */
export const IconAction: React.FC<{
  title: string;
  onClick: () => void;
  disabled?: boolean;
  color?: 'default' | 'primary' | 'error' | 'warning' | 'success';
  children: React.ReactElement;
}> = ({ title, onClick, disabled, color = 'default', children }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton size="small" color={color} onClick={onClick} disabled={disabled}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/** برچسب + مقدار، برای فهرست‌های «مشخصات» */
export const KV: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({ label, children }) => (
  <div className="ubc-kv"><span>{label}</span><b>{children}</b></div>
);

/** تیتر کوچک داخل کارت */
export const SubHead: React.FC<{ children: React.ReactNode; hint?: React.ReactNode }> = ({ children, hint }) => (
  <div className="ubc-subhead">
    <b>{children}</b>
    {hint && <span>{hint}</span>}
  </div>
);

export const Muted: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.8 }}>{children}</Typography>
);

// ───────────────────────────── واژگان مشترک ─────────────────────────────

export const STAGE_FA: Record<string, { label: string; color: string }> = {
  gate: { label: 'در حال بررسی', color: UB.info },
  waiting: { label: 'منتظر سرویس', color: UB.warn },
  skipped: { label: 'رد شد (مدل)', color: UB.muted },
  pending: { label: 'منتظر تأیید', color: UB.warn },
  approved: { label: 'در صف ارسال', color: UB.warn },
  sending: { label: 'در حال ارسال', color: UB.info },
  sent: { label: 'ارسال شد', color: UB.ok },
  rejected: { label: 'رد شد (ادمین)', color: UB.bad },
  expired: { label: 'منقضی', color: UB.accent },
  failed: { label: 'ناموفق', color: UB.bad },
};

export const INTENT_FA: Record<string, string> = {
  buy: 'قصد خرید',
  sell: 'قصد فروش',
  price: 'سؤال قیمت',
  safety: 'نگرانی امنیتی',
  howto: 'راهنمایی',
  followup: 'پیگیری',
  other: 'سایر',
  none: 'بی‌ربط',
};

export const ACCOUNT_STATUS_FA: Record<string, { label: string; color: string }> = {
  new: { label: 'وارد نشده', color: UB.muted },
  code_sent: { label: 'منتظر کد', color: UB.warn },
  password_needed: { label: 'منتظر رمز دومرحله‌ای', color: UB.warn },
  active: { label: 'فعال', color: UB.ok },
  disabled: { label: 'خاموش', color: UB.muted },
  error: { label: 'خطا / سشن باطل', color: UB.bad },
};

export const LEAD_STAGE_META: Record<string, { label: string; color: string }> = {
  new: { label: 'تازه', color: UB.muted },
  talking: { label: 'در حال گفتگو', color: UB.info },
  introduced: { label: 'کازماشاپ معرفی شد', color: UB.accent },
  interested: { label: 'پیگیر شد', color: UB.ok },
  cold: { label: 'سرد', color: '#94A3B8' },
};
